import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Currency } from '__generated__/globalTypes';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import { type FragmentOf, unmaskFragment } from 'lib/graphql';

import { TransactionAmountWithConversion } from '../../common/TransactionAmountWithConversion';
import { TransactionEntry, messages } from '../../common/TransactionEntry';
import { AccountEntryState } from '../AccountEntryState';
import type { AccountEntry_userAccountEntry } from './__generated__/index.graphql';

type Props = {
  title: string | ReactNode;
  userAccountEntry: FragmentOf<AccountEntry_userAccountEntry>;
  children?: ReactNode;
  tooltip?: ReactNode;
  icon?: ReactNode;
  expandable?: boolean;
  primaryCurrency?: Currency;
};

export const PriceContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  text-align: right;
`;

export { messages };

export const AccountEntry = ({
  title,
  userAccountEntry: userAccountEntry_,
  children,
  tooltip,
  icon,
  expandable,
  primaryCurrency,
}: Props) => {
  const userAccountEntry = unmaskFragment(userAccountEntry_);
  const { amounts } = userAccountEntry;
  const { toMonetaryAmount } = useMonetaryAmount();
  const monetaryOutput = toMonetaryAmount(unmaskFragment(amounts));
  const monetaryOutputIsPositive = monetaryOutput.isPositive;

  return (
    <TransactionEntry
      date={userAccountEntry.date}
      expandable={expandable}
      amount={
        <PriceContainer>
          {tooltip}
          <TransactionAmountWithConversion
            monetaryAmount={unmaskFragment(amounts)}
            primaryCurrency={primaryCurrency}
            aasmState={userAccountEntry.aasmState}
            positive={monetaryOutputIsPositive}
          />
        </PriceContainer>
      }
      state={<AccountEntryState userAccountEntry={userAccountEntry} />}
      title={title}
      icon={icon}
    >
      {children}
    </TransactionEntry>
  );
};

AccountEntry.fragments = {
  userAccountEntry: gql`
    fragment AccountEntry_userAccountEntry on UserAccountEntry {
      id
      aasmState
      amounts {
        ...useMonetaryAmount_monetaryAmount
      }
      date
      ...AccountEntryState_userAccountEntry
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${AccountEntryState.fragments.userAccountEntry}
  ` as TypedDocumentNode<AccountEntry_userAccountEntry>,
};
